import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Close, DeleteOutline, EditOutlined } from '@mui/icons-material'
import EmissionService from '../../../../shared/services/emission/emission.service'
import { IEmission } from '../../../../shared/services/emission/emission.model'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import { EmissionsTable } from './emissions-table'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked'
import { EditBulkUploadLinesDialog } from '../../../dialogs/settings/data-import/edit-bulk-upload-lines'
import { EditEmissionDialog } from '../../../dialogs/emissions/edit-emission-dialog'
import { CustomFilter } from '../../miscellaneous-components/custom-filter'
import { emissionsFilterConfig, EmissionsFilterConfigItem, FLAG_FILTER_MODELS } from '../../miscellaneous-components/custom-filter-config'
import { useFlagEmissionsCapability } from '../../../../hooks/use-flag-emissions-capability'
import { segmentedToggleSx } from '../../../../shared/utils/segmented-toggle-sx'
import { ISpendTotal } from '../../../../shared/services/emission/emission.model'
import { TableSummaryStats } from '../../miscellaneous-components/table-summary-stats'
import { formatEmissionAmount } from '../../../../shared/utils/emission-summary-utils'
import { spendTotalsAsStats } from '../../../../shared/utils/spend-totals-utils'

const emissionsFilterQueryParamMap: Record<string, string> = {
  category: 'categoryId',
  country: 'countryId',
  location: 'locationId',
  vehicle: 'vehicleId',
  project: 'projectId',
  contactSupplier: 'contactSupplierId',
  manufacturer: 'manufacturerId',
  scope: 'scope',
  measurementUnit: 'measurementUnitId',
  group: 'groupId',
  subsidiary: 'subsidiaryId',
  dataType: 'dataTypeId',
  emissionType: 'emissionType',
  isFlag: 'isFlag',
  flagCategory: 'flagCategory',
  emissionFactor: 'emissionFactorId',
  source: 'source',
}

function buildEmissionsFilterParams(filterValues: Record<string, any>) {
  const params = new URLSearchParams()

  if (Array.isArray(filterValues.customField) && filterValues.customField.length > 0) {
    const pairs = (filterValues.customField as string[])
      .map((s) => {
        const firstSep = s.indexOf('::')
        if (firstSep === -1) return null
        const fieldId = s.slice(0, firstSep)
        const rest = s.slice(firstSep + 2)
        const secondSep = rest.indexOf('::')
        const matchMode = secondSep !== -1 ? rest.slice(0, secondSep) : 'exact'
        const value = secondSep !== -1 ? rest.slice(secondSep + 2) : rest
        return { fieldId, value, matchMode }
      })
      .filter(Boolean)
    if (pairs.length > 0) params.set('customFieldFilters', JSON.stringify(pairs))
  }

  Object.entries(filterValues).forEach(([filterKey, value]) => {
    if (filterKey === 'customField') return
    const queryKey = emissionsFilterQueryParamMap[filterKey] ?? filterKey
    if (Array.isArray(value)) {
      const filteredValues = value.filter((item) => item !== undefined && item !== null && item !== '')
      if (filteredValues.length > 0) {
        params.set(queryKey, filteredValues.join(','))
      }
      return
    }

    if (value !== undefined && value !== null && value !== '') {
      params.set(queryKey, String(value))
    }
  })

  return params
}

function isFilterFixedByBaseQuery(filter: EmissionsFilterConfigItem, params: URLSearchParams) {
  if (filter.model === 'emissionType') {
    return params.has('emissionType') || params.has('spendOnly')
  }

  if (!filter.model) return false
  const queryParam = emissionsFilterQueryParamMap[filter.model]
  return queryParam ? params.has(queryParam) : false
}

interface IEmissionsProps {
  includeDelete?: boolean
  includeEdit?: boolean
  queryString?: string
  backLink?: string
  backText?: string
  toolbarActions?: ReactNode
  initialReportingFilter?: 'included' | 'excluded'
}

export const Emissions = ({
  includeDelete = false,
  includeEdit = false,
  queryString,
  backLink,
  backText,
  toolbarActions,
  initialReportingFilter = 'included',
}: IEmissionsProps) => {
  const emissionService = new EmissionService()
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const [emissions, setEmissions] = useState<IEmission[]>([])
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  const [inputPage, setInputPage] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)
  // Whole filtered set, not the page: the API sums every matching record.
  const [filteredTotalKg, setFilteredTotalKg] = useState(0)
  const [filteredSpendTotals, setFilteredSpendTotals] = useState<ISpendTotal[]>([])
  const [perPage, setPerPage] = useState(10)
  const [isTableLoading, setIsTableLoading] = useState(true)
  const [isButtonLoading, setIsButtonLoading] = useState(false)
  const [selected, setSelected] = useState<string[]>([])
  const [selectAllFiltered, setSelectAllFiltered] = useState(false)
  const [openBulkEditDialog, setOpenBulkEditDialog] = useState(false)
  const [editEmissionId, setEditEmissionId] = useState<string | undefined>()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'single', id: string } | { type: 'bulk' } | null>(null)
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({})
  const [appliedFilterNames, setAppliedFilterNames] = useState<Record<string, any>>({})
  const [reportingFilter, setReportingFilter] = useState<'included' | 'excluded'>(() => {
    if (queryString?.includes('excludeFromReporting=true')) return 'excluded'
    if (queryString?.includes('excludeFromReporting=false')) return 'included'
    return initialReportingFilter
  })
  const isLocked = !!useIsReportPeriodLocked()
  const { flagEnabled } = useFlagEmissionsCapability()
  const canManageEmissions = !!userDetails?.roles?.some(role => role === 'admin' || role === 'analyst')
  const canDelete = includeDelete && !isLocked && canManageEmissions
  const canEdit = includeEdit && !isLocked && canManageEmissions
  const showCheckbox = canDelete || canEdit

  const normalizedBaseQuery = useMemo(() => {
    const params = new URLSearchParams((queryString ?? '').replace(/^[?&]/, '').replace(/\?/g, '&'))
    params.delete('excludeFromReporting')
    return params
  }, [queryString])

  const availableFilters = useMemo(
    () =>
      emissionsFilterConfig.filter(
        (filter) =>
          !isFilterFixedByBaseQuery(filter, normalizedBaseQuery) &&
          (flagEnabled || !FLAG_FILTER_MODELS.includes(filter.model ?? ''))
      ),
    [normalizedBaseQuery, flagEnabled]
  )

  const filterQueryParams = useMemo(() => buildEmissionsFilterParams(appliedFilters), [appliedFilters])

  const effectiveQueryString = useMemo(() => {
    const params = new URLSearchParams(normalizedBaseQuery.toString())
    filterQueryParams.forEach((value, key) => {
      params.set(key, value)
    })
    params.set('excludeFromReporting', reportingFilter === 'excluded' ? 'true' : 'false')
    const query = params.toString()
    return query ? `&${query}` : ''
  }, [normalizedBaseQuery, filterQueryParams, reportingFilter])

  const effectiveBackLink = useMemo(() => {
    if (!backLink) return undefined
    const [path, search = ''] = backLink.split('?')
    const params = new URLSearchParams(search)
    params.set('emissionsTab', reportingFilter)
    const query = params.toString()
    return query ? `${path}?${query}` : path
  }, [backLink, reportingFilter])

  const effectiveSelectionCount = selectAllFiltered ? totalRecords : selected.length

  const allRowsOnPageSelected = useMemo(() => {
    if (emissions.length === 0) return false
    return emissions.every((e) => selected.includes(e.id))
  }, [emissions, selected])

  const showSelectAllFilteredBanner =
    showCheckbox &&
    !selectAllFiltered &&
    allRowsOnPageSelected &&
    totalRecords > emissions.length

  useEffect(() => {
    setSelected([])
    setSelectAllFiltered(false)
  }, [effectiveQueryString, reportPeriodContext.lastSelectedReportId])

  useEffect(() => {
    setAppliedFilters({})
    setAppliedFilterNames({})
    setInputPage(0)
  }, [queryString, reportPeriodContext.lastSelectedReportId])

  useEffect(() => {
    setSelected([])
    setSelectAllFiltered(false)
  }, [perPage])

  const handleEditBulk = () => {
    setOpenBulkEditDialog(true)
  }

  const closeBulkEditDialogHandler = () => {
    setOpenBulkEditDialog(false)
  }

  const successBulkEditDialogHandler = () => {
    setSelected([])
    setSelectAllFiltered(false)
    fetchEmissions()
    setOpenBulkEditDialog(false)
  }

  const handleEditRow = (id: string) => {
    setEditEmissionId(id)
  }

  const closeEditEmissionDialogHandler = () => {
    setEditEmissionId(undefined)
  }

  const successEditEmissionDialogHandler = () => {
    fetchEmissions()
    setEditEmissionId(undefined)
  }

  const showMenuItems = [
    'EDIT_ACTIVITY',
    'EDIT_CONVERSION',
    'EDIT_LOCATION',
    'EDIT_COUNTRY',
    'EDIT_SUBSIDIARY',
    'EDIT_PROJECT',
    'EDIT_VEHICLE',
    'EDIT_SUPPLIER',
    'EDIT_REGULATED_EMISSION_TRADING_SCHEME',
    'EDIT_EXCLUDE_FROM_REPORTING',
    'EDIT_FLAG_CLASSIFICATION',
  ]

  const fetchEmissions = async () => {
    if (!reportPeriodContext.lastSelectedReportId) {
      setEmissions([])
      setTotalRecords(0)
      setFilteredTotalKg(0)
      setFilteredSpendTotals([])
      if (reportPeriodContext.reportPeriodsLoaded) {
        setIsTableLoading(false)
      }
      return
    }
    setIsTableLoading(true)
    try {
      const result = await emissionService.getAll(
        reportPeriodContext.lastSelectedReportId,
        inputPage,
        perPage,
        orderBy,
        order,
        effectiveQueryString
      )
      setEmissions(result.rows)
      setTotalRecords(result.count)
      setFilteredTotalKg(result.filteredTotalEmissionsKg ?? 0)
      setFilteredSpendTotals(Array.isArray(result.filteredSpendTotals) ? result.filteredSpendTotals : [])
    } catch (error) {
      console.error(error)
    } finally {
      setIsTableLoading(false)
    }
  }

  useEffect(() => {
    fetchEmissions()
  }, [reportPeriodContext.lastSelectedReportId, inputPage, perPage, orderBy, order, effectiveQueryString])

  const handleDeleteRow = (id: string) => {
    setDeleteTarget({ type: 'single', id })
    setDeleteConfirmText('')
    setDeleteDialogOpen(true)
  }

  const buildDeleteMessage = (count: number) => {
    const base = `Are you sure you want to delete ${count.toLocaleString()} emissions? This action cannot be undone.`
    if (count > 500) {
      return `${base} This will remove a large number of records.`
    }
    return base
  }

  const handleDeleteBulk = () => {
    const count = effectiveSelectionCount
    if (count === 0) return
    setDeleteTarget({ type: 'bulk' })
    setDeleteConfirmText('')
    setDeleteDialogOpen(true)
  }

  const handleExitSelectAllFiltered = (remainingIdsOnPage: string[]) => {
    setSelectAllFiltered(false)
    setSelected(remainingIdsOnPage)
  }

  const handleFilterChange = (filters: Record<string, any>) => {
    setAppliedFilters(filters)
    setInputPage(0)
  }

  const handleAppliedFilterStateChange = (filters: Record<string, any>, filterNames: Record<string, any>) => {
    setAppliedFilters(filters)
    setAppliedFilterNames(filterNames)
  }

  const handleRemoveAppliedFilter = (filterKey: string, valueToRemove: string) => {
    const currentValue = appliedFilters[filterKey]
    const updatedFilterValues = { ...appliedFilters }
    const updatedFilterNames = { ...appliedFilterNames }

    if (Array.isArray(currentValue)) {
      const currentNames = Array.isArray(appliedFilterNames[filterKey]) ? [...appliedFilterNames[filterKey]] : []
      const valueIndex = currentValue.findIndex((value) => value === valueToRemove)
      const nextValues = currentValue.filter((value) => value !== valueToRemove)

      if (nextValues.length === 0) {
        delete updatedFilterValues[filterKey]
        delete updatedFilterNames[filterKey]
      } else {
        updatedFilterValues[filterKey] = nextValues
        if (valueIndex >= 0) currentNames.splice(valueIndex, 1)
        updatedFilterNames[filterKey] = currentNames
      }
    } else {
      delete updatedFilterValues[filterKey]
      delete updatedFilterNames[filterKey]
    }

    setAppliedFilters(updatedFilterValues)
    setAppliedFilterNames(updatedFilterNames)
    setInputPage(0)
  }

  const handleClearAppliedFilters = () => {
    setAppliedFilters({})
    setAppliedFilterNames({})
    setInputPage(0)
  }

  const filterChips = useMemo(() => {
    const chips: JSX.Element[] = []

    Object.entries(appliedFilterNames).forEach(([filterKey, displayNames]) => {
      const filter = availableFilters.find(f => (f.model || f.name) === filterKey)
      const filterLabel = filter?.name || filterKey

      if (Array.isArray(displayNames)) {
        displayNames.forEach((displayName, index) => {
          const correspondingId = Array.isArray(appliedFilters[filterKey]) ? appliedFilters[filterKey][index] : null
          if (!correspondingId) return

          chips.push(
            <Chip
              key={`${filterKey}-${correspondingId}`}
              label={`${filterLabel}='${displayName}'`}
              onDelete={() => handleRemoveAppliedFilter(filterKey, correspondingId)}
              deleteIcon={<Close />}
              variant="outlined"
              size="small"
            />
          )
        })
        return
      }

      if (!displayNames) return
      const correspondingId = appliedFilters[filterKey]
      if (!correspondingId) return

      chips.push(
        <Chip
          key={`${filterKey}-${correspondingId}`}
          label={`${filterLabel}='${displayNames}'`}
          onDelete={() => handleRemoveAppliedFilter(filterKey, correspondingId)}
          deleteIcon={<Close />}
          variant="outlined"
          size="small"
        />
      )
    })

    const totalFilterCount = Object.values(appliedFilterNames).reduce((total, displayNames) => {
      if (Array.isArray(displayNames)) return total + displayNames.length
      if (displayNames) return total + 1
      return total
    }, 0)

    if (totalFilterCount > 1) {
      chips.push(
        <Chip
          key="clear-all"
          label="Clear all"
          onClick={handleClearAppliedFilters}
          variant="filled"
          color="secondary"
          size="small"
          sx={{ cursor: 'pointer' }}
        />
      )
    }

    return chips
  }, [appliedFilterNames, appliedFilters, availableFilters])

  // Totals for the whole filtered set, next to the pagination. Without them a filtered view could
  // only be totalled by exporting it and adding the rows up in a spreadsheet, which is exactly what
  // people were doing. Emissions follow the list's own gate (selected calculations, upstream/WTT
  // excluded); spend is the entered spend per currency, never the FX or CPI converted figure.
  const summary = useMemo(() => {
    if (totalRecords === 0) return undefined
    return (
      <TableSummaryStats
        items={[
          { label: 'Records', value: totalRecords.toLocaleString('en-GB') },
          {
            label: 'Total emissions',
            value: filteredTotalKg === 0 ? '0.00 tCO₂e' : formatEmissionAmount(filteredTotalKg),
            hint: 'Sum of the selected calculations on every record matching your filters, across all pages. Upstream (WTT) emissions are not included, so this can read lower than the analytics totals.',
          },
          ...spendTotalsAsStats(filteredSpendTotals).map((stat, index) => ({
            ...stat,
            hint: index === 0 ? 'Spend as entered on every record matching your filters, before any exchange rate or inflation adjustment. Shown per currency.' : undefined,
          })),
        ]}
      />
    )
  }, [totalRecords, filteredTotalKg, filteredSpendTotals])

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setDeleteConfirmText('')
    setDeleteTarget(null)
  }

  const confirmDeleteHandler = () => {
    if (!reportPeriodContext.lastSelectedReportId || deleteConfirmText.trim().toUpperCase() !== 'DELETE') return

    setIsButtonLoading(true)
    const done = () => {
      setSelected([])
      setSelectAllFiltered(false)
      fetchEmissions()
      userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' })
      setIsButtonLoading(false)
      closeDeleteDialog()
    }
    const fail = (error: unknown) => {
      const message = (error as any)?.response?.data?.message || 'There was an error, please try again'
      userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
      console.error(error)
      setIsButtonLoading(false)
    }

    if (deleteTarget?.type === 'single') {
      emissionService.deleteEmissions([deleteTarget.id]).then(done).catch(fail)
      return
    }

    if (selectAllFiltered) {
      emissionService.deleteAllEmissions(reportPeriodContext.lastSelectedReportId, effectiveQueryString).then(done).catch(fail)
    } else {
      emissionService.deleteEmissions(selected).then(done).catch(fail)
    }
  }

  return (
    <Box>
      {showSelectAllFilteredBanner && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            The {emissions.length} rows on this page are selected.{' '}
            <Link
              component="button"
              type="button"
              underline="always"
              sx={{ cursor: 'pointer', verticalAlign: 'baseline' }}
              onClick={() => {
                setSelectAllFiltered(true)
                setSelected([])
              }}
            >
              Select all {totalRecords.toLocaleString()} records
            </Link>
            ?
          </Typography>
        </Alert>
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Stack
          spacing={1}
          sx={{ minWidth: 0 }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1}
            flexWrap='wrap'
          >
            {availableFilters.length > 0 && (
              <CustomFilter
                key={`emissions-filter-button-${reportPeriodContext.lastSelectedReportId ?? 'no-report'}-${queryString ?? 'base'}`}
                filters={availableFilters}
                filterValues={appliedFilters}
                onFilterChange={handleFilterChange}
                onAppliedFilterStateChange={handleAppliedFilterStateChange}
                buttonAlignment="left"
                showButtonOnly={true}
              />
            )}
            <ToggleButtonGroup
              value={reportingFilter}
              exclusive
              onChange={(_event, newValue) => {
                if (newValue) setReportingFilter(newValue)
              }}
              size='small'
              aria-label='Included or excluded emissions'
              sx={segmentedToggleSx}
            >
              <ToggleButton value='included' aria-label='Included emissions'>
                Included
              </ToggleButton>
              <ToggleButton value='excluded' aria-label='Excluded emissions'>
                Excluded
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {filterChips.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {filterChips}
            </Box>
          )}
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent='flex-end'
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          spacing={1}
          flexWrap='wrap'
        >
          {toolbarActions}
          {(canEdit || canDelete) && effectiveSelectionCount > 0 && (
            <>
              {canEdit && (
                <LoadingButton
                  variant="contained"
                  loading={isButtonLoading}
                  loadingPosition="start"
                  startIcon={<EditOutlined fontSize='small' />}
                  onClick={handleEditBulk}
                >
                  <Typography sx={{ pl: 0.5 }}>Edit ({effectiveSelectionCount.toLocaleString()})</Typography>
                </LoadingButton>
              )}
              {canDelete && (
                <LoadingButton
                  variant="outlined"
                  loading={isButtonLoading}
                  loadingPosition="start"
                  startIcon={<DeleteOutline fontSize='small' />}
                  onClick={handleDeleteBulk}
                >
                  <Typography sx={{ pl: 0.5 }}>Delete ({effectiveSelectionCount.toLocaleString()})</Typography>
                </LoadingButton>
              )}
            </>
          )}
        </Stack>
      </Stack>
      <EmissionsTable
        emissions={emissions}
        inputPage={inputPage}
        setInputPage={setInputPage}
        totalRecords={totalRecords}
        perPage={perPage}
        setPerPage={setPerPage}
        setOrderBy={setOrderBy}
        setOrder={setOrder}
        selected={selected}
        setSelected={setSelected}
        includeCheckbox={showCheckbox}
        selectAllFiltered={selectAllFiltered}
        onExitSelectAllFiltered={handleExitSelectAllFiltered}
        onDeleteRow={canDelete ? handleDeleteRow : undefined}
        onEditRow={canEdit ? handleEditRow : undefined}
        backLink={effectiveBackLink}
        backText={backText}
        loading={isTableLoading || !reportPeriodContext.reportPeriodsLoaded}
        summary={summary}
      />
      {editEmissionId && (
        <EditEmissionDialog
          open={!!editEmissionId}
          dialogDetailsId={editEmissionId}
          closeHandler={closeEditEmissionDialogHandler}
          successHandler={successEditEmissionDialogHandler}
        />
      )}
      {openBulkEditDialog && reportPeriodContext.lastSelectedReportId && (
        <EditBulkUploadLinesDialog
          open={openBulkEditDialog}
          closeHandler={closeBulkEditDialogHandler}
          dialogDetailsIds={selectAllFiltered ? [] : selected}
          successHandler={successBulkEditDialogHandler}
          showMenuItems={showMenuItems}
          calculationEditCodes={['EDIT_ACTIVITY', 'EDIT_CONVERSION']}
          reportPeriodId={reportPeriodContext.lastSelectedReportId}
          isForTransactions={true}
          isEditAll={selectAllFiltered}
          queryString={effectiveQueryString}
        />
      )}
      <Dialog maxWidth='sm' fullWidth open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <Box sx={{ p: 4 }}>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>Confirmation</DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <Typography variant='body1' sx={{ mb: 2 }}>
              {deleteTarget?.type === 'single'
                ? 'Delete 1 emission? This action cannot be undone.'
                : buildDeleteMessage(effectiveSelectionCount)}
            </Typography>
            <Typography variant='body2' sx={{ mb: 1 }}>
              Type <strong>DELETE</strong> to confirm.
            </Typography>
            <TextField
              fullWidth
              size='small'
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder='DELETE'
            />
          </DialogContent>
          <DialogActions sx={{ pt: 2, pl: 0, pr: 0 }}>
            <Stack direction='row' spacing={2}>
              <Button variant='text' onClick={closeDeleteDialog}>
                <Typography sx={{ p: 0.5 }}>Cancel</Typography>
              </Button>
              <Button
                variant='contained'
                disableElevation
                onClick={confirmDeleteHandler}
                disabled={deleteConfirmText.trim().toUpperCase() !== 'DELETE' || isButtonLoading}
              >
                <Typography>Continue</Typography>
              </Button>
            </Stack>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  )
}
